import React, { useState, useEffect, useCallback } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import { useHistory } from 'react-router-dom';
import Media from '../../components/RetailVdp/Media';
import RetailService from '../../services/RetailService';
import ROUTES from '../../components/Routes/constants';
import Icon from '../../_shared/components/Icon';
import Alert from '../../_shared/components/Alert';
import mixpanelConfig from '../../_shared/constants/mixpanelConfig';
import ReducerConstants from '../../_shared/constants/reducers';
import {
  RetailMediaType,
  RETAIL_INPUTS,
  UserSubscriptionPlan
} from '../../_shared/constants/retail';
import useTradeDetails from '../../_shared/hooks/trade/useTradeDetails';
import MEDIA_TYPE from '../../_shared/constants/media';
import useMediaList from '../../_shared/hooks/media/useMediaList';
import { isMobileDevice } from '../../_shared/utils/userAgent';
import Loading from './Loading';
import MixpanelService from '../../services/MixpanelService';
import Notes from '../../components/RetailVdp/Notes';
import Input from '../../components/RetailVdp/Input';
import SettingsService from '../../services/SettingsService';
import useFeatureFlags from '../../_shared/hooks/useFeatureFlags';
import FEATURE_FLAGS from '../../_shared/constants/featureFlags';

import {
  RetailContainer,
  Main,
  Header,
  Title,
  SubHeading,
  Form,
  FormTitle,
  FormLabel,
  SelectAllButton,
  Section,
  SectionHeader,
  PriceContainer,
  PriceInput,
  PriceDisclaimer,
  VehiclePhotosContainer,
  ToggleContainer,
  StyledToggle,
  DealershipContainer,
  Footer,
  CreatePageDescription,
  StyledCreatePageButton,
  CloseButton,
  StyledImageLabel,
  StyledImageAndLabelContainer,
  FooterText,
  MainContainer
} from './Styles';
import TRToast from '../../_shared/helpers/TRToast';

const RetailCreate = ({ isOmp = false, tradeId, handleClose = () => {} }) => {
  const handleCloseCb = useCallback(handleClose, [handleClose]);
  const settingsService = SettingsService.get();
  const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const priceRegex = /^[0-9\b]+$/;

  const inputNotesRef = React.createRef();
  const inputPriceRef = React.createRef();

  const mp = MixpanelService.get();

  const history = useHistory();
  const user = useSelector(
    state => state[ReducerConstants.AUTH]?.user,
    shallowEqual
  );
  const tradeDetails = useTradeDetails(tradeId);
  const mediaList = useMediaList(tradeId);

  const intl = useIntl();
  const isTIAHWhiteLabelEnabled = useFeatureFlags(
    FEATURE_FLAGS.IS_TIAH_WHITE_LABEL_ENABLED
  );
  const [showDamages, setShowDamages] = useState(false);
  const [showVideos, setShowVideos] = useState(false);
  const [retailMediaList, setRetailMediaList] = useState([]);
  const [isCreatingRetailCase, setIsCreatingRetailCase] = useState(false);
  const [isStandardUser, setIsStandardUser] = useState(false);
  const [dealerRetailInfo, setDealerRetailInfo] = useState(null);
  const [dealershipDetails, setDealershipDetails] = useState(null);
  const [selectAll, setSelectAll] = useState({
    IMAGE: false,
    DAMAGE: false,
    VIDEO: false
  });

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Escape') {
        mp.track(mixpanelConfig.names.escCloseModal, {
          source: 'Retail create'
        });
        event.preventDefault();
        history.goBack();
        return false;
      }
      return true;
    },
    [history, mp]
  );

  useEffect(() => {
    const getSubscriptionDetails = async () => {
      const subscriptionResponse = await settingsService.getProSubscriptionDetails();
      setIsStandardUser(
        subscriptionResponse?.title === UserSubscriptionPlan.STANDARD
      );
    };

    getSubscriptionDetails();
  }, [settingsService]);

  useEffect(() => {
    const getDealershipRetailFields = async () => {
      const dealerRetailInfoResponse = await settingsService.getDealerRetailInfo();
      if (dealerRetailInfoResponse?.status === 200)
        setDealerRetailInfo(dealerRetailInfoResponse?.data);
    };

    getDealershipRetailFields();
  }, [settingsService]);

  useEffect(() => {
    const dealershipDetailsList = [
      {
        id: RETAIL_INPUTS.NAME,
        label: intl.formatMessage({
          id: 'retail.customization.dealerDetails.name'
        }),
        placeholder: intl.formatMessage({
          id: 'retail.customization.dealerDetails.name.placeholder'
        }),
        type: 'text',
        size: 216,
        max: 216,
        ref: React.createRef(),
        value: dealerRetailInfo?.retailVdp?.salespersonName
      },
      {
        id: RETAIL_INPUTS.EMAIL,
        label: intl.formatMessage({
          id: 'retail.customization.dealerDetails.email'
        }),
        placeholder: intl.formatMessage({
          id: 'retail.customization.dealerDetails.email.placeholder'
        }),
        type: 'email',
        size: 360,
        max: 360,
        ref: React.createRef(),
        value: dealerRetailInfo?.retailVdp?.salespersonEmail
      },
      {
        id: RETAIL_INPUTS.PHONE,
        label: intl.formatMessage({
          id: 'retail.customization.dealerDetails.phone'
        }),
        placeholder: intl.formatMessage({
          id: 'retail.customization.dealerDetails.phoneNumber.placeholder'
        }),
        type: 'tel',
        size: 216,
        max: '12',
        ref: React.createRef(),
        value: dealerRetailInfo?.retailVdp?.salespersonPhone
      },
      {
        id: RETAIL_INPUTS.INVENTORY,
        label: intl.formatMessage({
          id: 'retail.customization.dealerDetails.inventory'
        }),
        placeholder: intl.formatMessage({
          id: 'retail.customization.dealerDetails.inventory.placeholder'
        }),
        type: 'url',
        size: 360,
        max: 1094,
        ref: React.createRef(),
        value: dealerRetailInfo?.retailVdp?.dealershipLink
      }
    ];
    setDealershipDetails(dealershipDetailsList);
  }, [dealerRetailInfo, intl]);

  useEffect(() => {
    if (
      retailMediaList?.length === 0 &&
      mediaList?.meta?.nonDamagePhotos?.length > 0
    ) {
      const mediaInitialList = [];
      mediaList.meta.nonDamagePhotos.forEach(photo => {
        mediaInitialList.push({
          id: photo.id,
          label: photo?.imageLocation?.displayName,
          url: photo?.externalUrl,
          mediaType: RetailMediaType.IMAGE,
          previewUrl: photo?.previewUrl,
          imageLocationId: photo?.imageLocation?.id,
          isChecked: true
        });
      });
      setRetailMediaList(mediaInitialList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaList?.meta?.nonDamagePhotos]);

  useEffect(() => {
    if (!isCreatingRetailCase) {
      document.addEventListener('keydown', handleKeyDown, true);
    } else {
      document.removeEventListener('keydown', handleKeyDown, true);
    }

    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [handleKeyDown, isCreatingRetailCase]);

  const onDamageToggleClick = () => {
    setShowDamages(show => !show);
    mediaList.meta.disclosurePhotos.forEach(photo => {
      const idx = retailMediaList.findIndex(ele => ele.id === photo.id);
      if (idx === -1) {
        retailMediaList.push({
          id: photo.id,
          label: photo?.displayName,
          url: photo?.externalUrl,
          mediaType: RetailMediaType.DAMAGE,
          previewUrl: photo?.previewUrl,
          isChecked: !showDamages
        });
      } else {
        retailMediaList[idx].isChecked = !showDamages;
      }
    });
  };

  const onVideoToggleClick = () => {
    setShowVideos(show => !show);
    mediaList.videos.forEach(video => {
      const idx = retailMediaList.findIndex(ele => ele.id === video.id);
      if (idx === -1) {
        retailMediaList.push({
          id: video.id,
          label: RetailMediaType.VIDEO,
          url: video?.externalUrl,
          mediaType: RetailMediaType.VIDEO,
          previewUrl: video?.previewUrl,
          isChecked: !showVideos
        });
      } else {
        retailMediaList[idx].isChecked = !showVideos;
      }
    });
  };

  const createRetailPage = async () => {
    const customizationDetails = {};
    const idx = retailMediaList.findIndex(
      ele => ele.mediaType === RetailMediaType.IMAGE && ele.isChecked === true
    );
    if (idx === -1) {
      // Implying none of the overview images were selected
      TRToast(
        <Alert
          variant="error"
          messages={[
            <FormattedMessage id="retail.customization.image.input.error" />
          ]}
        />
      );
      return;
    }

    const nameRef = dealershipDetails[0].ref?.current?.value;
    const emailRef = dealershipDetails[1].ref?.current?.value;
    const phoneRef = dealershipDetails[2].ref?.current?.value;
    const linkRef = isTIAHWhiteLabelEnabled
      ? dealerRetailInfo?.retailVdp?.dealershipLink
      : dealershipDetails[3].ref?.current?.value;

    if (
      (phoneRef && phoneRef.length !== 12) ||
      (emailRef && !emailRegex.test(emailRef)) ||
      (inputPriceRef?.current?.value &&
        !priceRegex.test(inputPriceRef?.current?.value))
    ) {
      TRToast(
        <Alert
          variant="error"
          messages={[
            <FormattedMessage id="retail.customization.input.error" />
          ]}
        />
      );
      return;
    }
    setIsCreatingRetailCase(true);
    customizationDetails[RETAIL_INPUTS.NOTES] = inputNotesRef?.current?.value;
    customizationDetails[RETAIL_INPUTS.BLUR_BACKGROUND] = false;
    customizationDetails[RETAIL_INPUTS.NAME] = nameRef;
    customizationDetails[RETAIL_INPUTS.EMAIL] = emailRef;
    customizationDetails[RETAIL_INPUTS.PHONE] = phoneRef.replace(/-/gi, '');
    customizationDetails[RETAIL_INPUTS.INVENTORY] = linkRef;
    customizationDetails[RETAIL_INPUTS.PRICE] = inputPriceRef?.current?.value;

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    let retailVdpRoute;
    if (!isMobileDevice()) {
      retailVdpRoute = window.open(ROUTES.RETAIL_LOADING, '_blank');
    }

    const retailResponse = await RetailService.createRetailCase(
      tradeId,
      customizationDetails,
      tradeDetails,
      user?.isCanSell,
      retailMediaList
    );

    mp.trackRetailPublicCreate({
      guid: retailResponse?.guid,
      tradeDetails,
      retailMediaList,
      originalMediaList: mediaList,
      retailInputRequest: customizationDetails,
      isMobile: isMobileDevice(),
      showDamages,
      showVideos
    });

    // Go to Next Retail Page if retailResponse success
    if (Object.keys(retailResponse).length && retailResponse?.guid) {
      setIsCreatingRetailCase(false);
      const retailPublicLink = `${ROUTES.RETAIL_PUBLIC.replace(
        ':guid',
        retailResponse.guid
      )}`;
      if (retailVdpRoute?.location) {
        retailVdpRoute.location.href = retailPublicLink;
      } else {
        window.open(`${retailPublicLink}`, `${!isSafari ? '_self' : '_blank'}`);
      }
    } else {
      setIsCreatingRetailCase(false);
      retailVdpRoute?.close();
      // TODO: refactor after error handling requirements discussed
      throw new Error('Error creating retail page');
    }
  };

  const handleCloseClick = () => {
    if (isOmp) {
      handleCloseCb();
    } else {
      history.goBack();
    }
    return false;
  };

  // Select/Deselect all within a section
  const onSelectAll = (event, sectionId) => {
    event.preventDefault();

    setRetailMediaList(listOfMedia => {
      // Set each section's state
      setSelectAll(select => ({
        ...select,
        [MEDIA_TYPE[sectionId]]: !selectAll[MEDIA_TYPE[sectionId]]
      }));

      // Perform the select/deselect all
      return listOfMedia.map(media => {
        if (media.mediaType !== MEDIA_TYPE[sectionId]) return media;

        return {
          ...media,
          isChecked: selectAll[MEDIA_TYPE[sectionId]]
        };
      });
    });
  };

  return (
    <RetailContainer variant={isOmp ? 'omp' : ''}>
      {isCreatingRetailCase && (
        <Loading
          closeModal={() => {
            setIsCreatingRetailCase(false);
          }}
        />
      )}
      <Main
        data-testid="retailCustomization"
        isStandardUser={
          isTIAHWhiteLabelEnabled && isStandardUser && !isMobileDevice()
        }
      >
        <CloseButton onClick={handleCloseClick} data-testid="retailCust-close">
          <FormattedMessage id="list.nav.searchbar.modal.closeButton" />
          <CloseButton.Icon />
        </CloseButton>
        <MainContainer>
          <Header>
            <Title data-testid="retailCust-headerTitle">
              {/* Share this vehicle with a customer */}
              <FormattedMessage id="retail.customization.heading" />
            </Title>
            <SubHeading data-testid="retailCust-SubHeaderTitle">
              <FormattedMessage id="retail.customization.subheading" />
            </SubHeading>
          </Header>
          <Form>
            <FormTitle data-testid="retailCust-FormTitle">
              {/* Customize Vehicle */}
              <FormattedMessage id="retail.customization.form.title" />
            </FormTitle>
            <FormLabel data-testid="retailCust-price">
              {/* Customer price (optional) */}
              <FormattedMessage id="retail.customization.price" />
            </FormLabel>
            <PriceContainer>
              <PriceInput>$</PriceInput>
              <Input
                inputId={RETAIL_INPUTS.PRICE}
                placeholder={intl.formatMessage({
                  id: 'vdp.retail.price.placeholder'
                })}
                type="number"
                pattern="^-?[0-9]\d*\.?\d*$"
                ref={inputPriceRef}
              />
            </PriceContainer>
            <PriceDisclaimer data-testid="retailCust-priceDisc">
              {/* Not including tax & licensing */}
              <FormattedMessage id="retail.customization.price.disclaimer" />
            </PriceDisclaimer>
            {mediaList?.meta?.nonDamagePhotos && (
              <Section>
                <SectionHeader>
                  <FormLabel>
                    {/* Vehicle images */}
                    <FormattedMessage id="retail.customization.media.vehicle.heading" />
                  </FormLabel>
                  <SelectAllButton
                    data-testid="retail-create-overview-select"
                    onClick={e => onSelectAll(e, MEDIA_TYPE.IMAGE)}
                  >
                    <FormattedMessage
                      id={`retail.customization.media.${
                        selectAll.IMAGE ? 'select' : 'deselect'
                      }`}
                    />
                  </SelectAllButton>
                </SectionHeader>
                <VehiclePhotosContainer>
                  {mediaList?.meta?.nonDamagePhotos.map(media => (
                    <StyledImageAndLabelContainer key={media.id}>
                      <Media
                        idkey={media.id}
                        media={media}
                        mediaType={MEDIA_TYPE.IMAGE}
                        retailMediaList={retailMediaList}
                        setSelectAll={setSelectAll}
                      />
                      <StyledImageLabel>
                        {media?.imageLocation?.displayName}
                      </StyledImageLabel>
                    </StyledImageAndLabelContainer>
                  ))}
                </VehiclePhotosContainer>
              </Section>
            )}
            {mediaList?.meta?.disclosurePhotos?.length > 0 && (
              <Section>
                <SectionHeader>
                  <ToggleContainer>
                    <StyledToggle
                      testId="retailCreate-damage-toggle"
                      onClick={() => onDamageToggleClick()}
                    />
                    <FormLabel>
                      {/* Include damage images */}
                      <FormattedMessage id="retail.customization.media.damage.heading" />
                    </FormLabel>
                  </ToggleContainer>
                  <SelectAllButton
                    onClick={e => onSelectAll(e, MEDIA_TYPE.DAMAGE)}
                  >
                    {showDamages && (
                      <FormattedMessage
                        id={`retail.customization.media.${
                          selectAll.DAMAGE ? 'select' : 'deselect'
                        }`}
                      />
                    )}
                  </SelectAllButton>
                </SectionHeader>
                {showDamages && (
                  <VehiclePhotosContainer>
                    {mediaList?.meta?.disclosurePhotos.map(photo => (
                      <StyledImageAndLabelContainer key={photo.id}>
                        <Media
                          idkey={photo.id}
                          media={photo}
                          mediaType={MEDIA_TYPE.DAMAGE}
                          retailMediaList={retailMediaList}
                          setSelectAll={setSelectAll}
                        />
                        <StyledImageLabel>
                          {photo?.displayName}
                        </StyledImageLabel>
                      </StyledImageAndLabelContainer>
                    ))}
                  </VehiclePhotosContainer>
                )}
              </Section>
            )}
            {mediaList?.videos?.length > 0 && (
              <Section>
                <SectionHeader>
                  <ToggleContainer>
                    <StyledToggle
                      testId="retailCreate-video-toggle"
                      onClick={() => onVideoToggleClick()}
                    />
                    <FormLabel>
                      <FormattedMessage id="retail.customization.media.video.heading" />
                    </FormLabel>
                  </ToggleContainer>
                  <SelectAllButton
                    onClick={e => onSelectAll(e, MEDIA_TYPE.VIDEO)}
                  >
                    {showVideos && (
                      <FormattedMessage
                        id={`retail.customization.media.${
                          selectAll.VIDEO ? 'select' : 'deselect'
                        }`}
                      />
                    )}
                  </SelectAllButton>
                </SectionHeader>
                {showVideos && (
                  <VehiclePhotosContainer>
                    {mediaList?.videos.map(video => (
                      <StyledImageAndLabelContainer key={video.id}>
                        <Media
                          idkey={video.id}
                          media={video}
                          mediaType={MEDIA_TYPE.VIDEO}
                          retailMediaList={retailMediaList}
                          setSelectAll={setSelectAll}
                        />
                        {video?.metadata?.length > 0 &&
                          video?.metadata[0]?.data &&
                          video?.metadata[0]?.data[0]?.key ===
                            'displayName' && (
                            <StyledImageLabel>
                              {video.metadata[0].data[0].value}
                            </StyledImageLabel>
                          )}
                      </StyledImageAndLabelContainer>
                    ))}
                  </VehiclePhotosContainer>
                )}
              </Section>
            )}
            <Section>
              <FormLabel>
                <FormattedMessage id="retail.customization.description.heading" />
              </FormLabel>
              <Notes ref={inputNotesRef} />
            </Section>
            <Section id="retail-dealership">
              <FormTitle>
                {/* Dealership details (optional) */}
                <FormattedMessage id="retail.customization.dealerDetails.heading" />
              </FormTitle>
              <DealershipContainer>
                {dealershipDetails &&
                  dealershipDetails?.map(field => {
                    return isTIAHWhiteLabelEnabled &&
                      field.id === RETAIL_INPUTS.INVENTORY ? null : (
                      <li key={field.id}>
                        <FormLabel
                          data-testid={`retailCust-dealer-${field?.id}-label`}
                          htmlFor={field.id}
                        >
                          {field.label}
                        </FormLabel>
                        <Input
                          inputId={field.id}
                          placeholder={field.placeholder}
                          type={field.type}
                          maxLength={field.max}
                          width={field.size}
                          ref={field.ref}
                          defaultValue={field.value}
                        />
                      </li>
                    );
                  })}
              </DealershipContainer>
            </Section>
            <Footer>
              <CreatePageDescription>
                <FormattedMessage id="retail.customization.create.description" />
              </CreatePageDescription>
              <StyledCreatePageButton
                id="retail-create-page-id"
                testId="retail-create-page"
                onClick={e => {
                  createRetailPage();
                  e.preventDefault();
                }}
              >
                {/* Create Page */}
                <FormattedMessage id="retail.customization.create.button" />
                <Icon icon="TR5-Create_page" />
              </StyledCreatePageButton>
            </Footer>
            {!isOmp && (
              <FooterText>
                <FormattedMessage id="retail.customization.footer" />
              </FooterText>
            )}
          </Form>
        </MainContainer>
      </Main>
    </RetailContainer>
  );
};

export default RetailCreate;
